<i18n src="@/locales/components/CbsDynamicFormConfig.json"></i18n>

<template>
	<div class="add-options">
		<draggable
			v-validate="'required'"
			name="add-options-config"
			:delay="200"
			:disabled="!isEditable"
			:delayOnTouchOnly="true"
			v-model="localOptionsValue"
			:handle="'.item__btn-drag'"
			:data-vv-as="$t('FieldConfig.ListOptions')"
			@end="$emit('input', localOptionsValue)"
		>
			<transition-group tag="ul" name="slide-fade" class="list-options add-options__list-options">
				<li
					class="item list-options__item mb-3 border-solid border border-gray-6 flex items-center justify-between"
					v-for="(option, index) in localOptionsValue"
					:key="option.id"
					@click="handleToggleEditMode(option)"
				>
					<div class="flex flex-1 items-center">
						<i class="item__btn-drag nucleo nucleo-ic_drag_handle_24px text-gray-3 text-xl cursor-move" />
						<cbs-input
							:class="[
								'w-full item__input',
								{
									'item__input--error': errors.first(`option-${option.id}`) || (option.id === currentEditOption.id && currentEditOption.isDuplicateContent)
								}
							]"
							v-validate="'required'"
							:value="option.content"
							:name="`option-${option.id}`"
							:ref="`option${option.id}Ref`"
							:data-vv-as="$t('FieldConfig.OptionContent')"
							:readonly="option.id !== currentEditOption.id"
							:placeholder="errors.first(`option-${option.id}`)"
							@input="val => handleCheckDuplicateOptionContentEditMode(val, index)"
							@keyup.enter="handleOnEnterEditMode(`option${option.id}Ref`)"
							@blur="handleCheckOnBlurOptionContentEditMode(option.content)"
						/>
					</div>
					<i class="nucleo nucleo-trash-can cursor-pointer" v-if="isEditable" @click.stop="showDeleteConfirm(index)" />
				</li>
			</transition-group>
		</draggable>

		<div class="add-options__input">
			<cbs-input class="w-full" v-model="newOption" :placeholder="inputPlaceholder" @keyup.enter="handleAddNewOption" @input="handleCheckDuplicateOptionContent" />
			<span class="text-danger text-sm" v-if="isDuplicateOption">{{ $t("FieldConfig.DuplicateOption") }}</span>
		</div>
		<span class="text-danger text-sm">{{ errors.first("add-options-config") }}</span>
	</div>
</template>

<script>
import Draggable from "vuedraggable";
import { showConfirm } from "@/mixins/mixins.js";

export default {
	inheritAttrs: false,
	inject: ["parentValidator"],
	name: "AddOptions",
	mixins: [showConfirm],
	components: { Draggable },
	props: {
		value: {
			type: Object,
			required: true,
			validator: prop => prop.hasOwnProperty("options") && Array.isArray(prop["options"])
		},
		isEditable: {
			type: Boolean,
			default: false
		},
		inputPlaceholder: String
	},
	data() {
		return {
			newOption: "",
			currentId: 1,
			currentEditOption: {
				id: null,
				content: "",
				isDuplicateContent: false
			},
			isDuplicateOption: false
		};
	},
	computed: {
		localOptionsValue: {
			get() {
				return this.value.options.map(option => {
					if (option.isNewData) {
						return { ...option, id: option._tempId };
					}
					return option;
				});
			},
			set(val) {
				this.$emit("input", { 
					...this.value, options: val.map(option => {
						if (option.isNewData) {
							return { ...option, id: null, _tempId: option.id };
						}
						return option;
					})
				});
			}
		},
		prefixOptionId() {
			return "cbs-option-id";
		}
	},
	methods: {
		handleAddNewOption() {
			if (this.newOption.trim() && !this.isDuplicateOption) {
				this.localOptionsValue = [ ...this.localOptionsValue, { id: `${this.prefixOptionId}-${this.currentId}`, content: this.newOption, isNewData: true }];
				this.newOption = "";
				this.currentId += 1;
			}
		},
		handleCheckDuplicateOptionContent(value) {
			if (value) {
				this.isDuplicateOption = this.checkDuplicateOption(this.newOption);
			}
		},
		showDeleteConfirm(optionIndex) {
			this.mixinShowConfirm().then(isAccept => {
				if (isAccept) {
					this.localOptionsValue = this.localOptionsValue.filter((option, index) => index !== optionIndex);
				}
			});
		},
		handleToggleEditMode(option) {
			this.currentEditOption = {
				id: option.id,
				content: option.content
			};
		},
		handleCheckOnBlurOptionContentEditMode(value) {
			if (!value.trim() || this.currentEditOption.isDuplicateContent) {
				this.localOptionsValue = this.localOptionsValue.map(option => {
					if (option.id === this.currentEditOption.id) {
						return {
							...option,
							content: this.currentEditOption.content
						};
					}
					return option;
				});
				this.$validator.reset();
				this.currentEditOption.isDuplicateContent = false;
			}
		},
		handleCheckDuplicateOptionContentEditMode(value, itemIndex) {
			this.localOptionsValue = this.localOptionsValue.map((localOption, index) => {
				if (itemIndex === index) {
					return {
						...localOption,
						content: value
					};
				}
				return localOption;
			});
			if (value) {
				this.currentEditOption = { ...this.currentEditOption, isDuplicateContent: this.checkDuplicateOption(value, this.currentEditOption.id) };
			}
		},
		handleOnEnterEditMode(ref) {
			if (this.$refs[ref] && this.$refs[ref][0] && this.$refs[ref][0].$el && this.$refs[ref][0].$el.children[0] && this.$refs[ref][0].$el.children[0].children[0]) {
				this.$refs[ref][0].$el.children[0].children[0].blur();
			}
		},
		checkDuplicateOption(compareOptionContent, excludeId = null) {
			return (
				compareOptionContent &&
				!!this.localOptionsValue.find(option => {
					const isDuplicateContent = option.content.trim() === compareOptionContent.trim();
					if (excludeId) {
						return isDuplicateContent && excludeId !== option.id;
					}
					return isDuplicateContent;
				})
			);
		}
	},
	created() {
		if (this.parentValidator) {
			this.$validator = this.parentValidator;
		}
	}
};
</script>

<style lang="scss" scoped>
.add-options {
	.list-options__item,
	::v-deep .add-options__input input {
		padding: 0 0.7rem;
		border-radius: 4px !important;
		border: 1px solid var(--color-gray-6) !important;
		box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.05) !important;
	}

	.list-options__item {
		::v-deep .item__input input,
		::v-deep .vs-input--placeholder {
			border: 0 !important;
			box-shadow: none !important;
		}

		.item__input--error {
			::v-deep input,
			::v-deep .vs-input--placeholder {
				color: var(--color-solid-red) !important;
			}
		}
	}
}
</style>
